import React, { useEffect, useRef } from "react";
import { GridStack } from "gridstack";
import GridItem from "../../../Components/UiComponents/GridItem/GridItem";
import DMSHeader from "../DMSHeader/DMSHeader";
import UserTaskTable from "./UserTaskTable/UserTaskTable";
import { useTranslation } from "react-i18next";

/**
 * UserTaskManagement Component
 * 
 * A dashboard layout component that manages user tasks using a grid system.
 * Features:
 * - Grid-based layout using GridStack
 * - Task management header with refresh functionality
 * - Integrated UserTaskTable for displaying and managing tasks
 * - Internationalization support
 * - CSV export capability
 */
const UserTaskManagement = (props) => {
  const { t } = useTranslation();
  
  // Ref to access UserTaskTable's refresh functionality
  const userTaskTableRef = useRef();

  /**
   * Initialize GridStack on component mount
   * GridStack provides drag-and-drop, resizable grid layout functionality
   */
  useEffect(() => {
    GridStack.init();
  }, []);

  /**
   * Handle refresh button click
   * Triggers task list refresh in the UserTaskTable component
   */
  const handleRefreshDailyTasks = () => {
    if (userTaskTableRef.current) {
      userTaskTableRef.current.refreshTaskList();
    }
  };

  /**
   * Grid layout configuration
   * Defines the position, size, and content of each grid item
   */
  const layoutData = [
    {
      // Header section - full width at top
      x: 0,
      y: 0,
      width: 12,
      height: 1,
      id: 1,
      component: (
        <DMSHeader
          title={
            <div className="d-flex justify-content-between align-items-center">
              {/* Page title */}
              <span className="col-auto me-auto">
                {t("task_management_header")}
              </span>
              
              {/* Refresh button with icon */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  handleRefreshDailyTasks();
                }}
                style={{
                  background: "#1a7a73",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  color: "white",
                  marginLeft: "10px",
                  padding: "4px 8px",
                  borderRadius: "4px",
                }}
                title={t("refresh_daily_tasks_tooltip") || "Refresh Daily Tasks"}
              >
                {/* Refresh icon SVG */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
              </button>
            </div>
          }
          showdownload={true}
          fileName="TaskManagement.csv"
          showmenu={true}
          showImportDropDownLabel={false}
        />
      ),
      haveOverflowVisible: true,
    },
    {
      // Main content section - full width below header
      x: 0,
      y: 1,
      width: 12,
      height: 11,
      id: 2,
      component: <UserTaskTable ref={userTaskTableRef} />,
    },
  ];

  return (
    <div className="grid-stack grid-stack-instance-1 smartpole__header--transition">
      {/* Render grid items using the layout configuration */}
      <GridItem layoutData={layoutData} />
    </div>
  );
};

export default UserTaskManagement;